// ===== Quantas caixas, que volume, que peso =====
//
// Levantamento: docs/superpowers/specs/2026-09-17-cotacao-de-frete-jamef.md
//
// POR QUE ISTO EXISTE: a API da Jamef exige `metragemCubica`, e o produto no
// Nomus nao tem dimensao nenhuma -- medido em 17/09: 28 campos, zero de medida.
// O que existe sao as regras de embalagem da PDLED, na planilha
// "CALCULADORA DE PESO E VOLUMES PARA COTACAO.xlsx". Este modulo aplica essas
// regras e devolve o que a cotacao precisa.
//
// E funcao PURA de proposito: nenhuma chamada de rede, nenhum acesso a banco.
// O risco do frete mora aqui, nao no HTTP, entao aqui e onde os testes pegam.
//
// O QUE ELE NAO E: uma simulacao fiel da expedicao. A propria planilha avisa --
// "Essa calculadora fornece uma visao aproximada do que tende a ser o resultado.
// A realidade do que e feito, muitas vezes sera diferente." Quem chamar isto tem
// de apresentar o numero como ESTIMATIVA.

import { CAIXAS_EMBALAGEM, PESOS_PERFIL, pesoUnitarioItem } from './pesos.jsx';

/** Peso maximo de mercadoria por caixa, em kg. Regra geral da planilha. */
export const MAX_KG_POR_CAIXA = 17;

/** Comprimento maximo de uma barra de perfil, em metros (Kaio, 17/09). */
export const MAX_CORTE_M = 3;

/**
 * As caixas, derivadas de `src/pesos.jsx`.
 *
 * NAO redeclaro a tabela aqui. A primeira versao deste modulo tinha a propria
 * copia das seis caixas -- eu escrevi sem antes procurar, e `pesos.jsx` ja
 * existia com as mesmas medidas, tara e comprimento maximo, em uso por
 * proposta-fiscal.jsx e proposta.jsx desde antes. Duas copias da mesma tabela
 * divergem no dia em que uma caixa muda, e o frete passa a discordar da tela de
 * volumes sem ninguem entender por que.
 *
 * `vao` e o comprimento util: a barra so entra se couber nele. `longa` separa as
 * caixas de perfil das de fonte e amostra.
 */
export const CAIXAS = CAIXAS_EMBALAGEM.map((c) => ({
  id: c.nome === 'CAIXA QUADRADA' ? 'QUADRADA'
    : c.nome === 'CAIXA DE AMOSTRA 1' ? 'AMOSTRA_1'
    : c.nome === 'CAIXA DE AMOSTRA 2' ? 'AMOSTRA_2'
    : c.nome,
  nome: c.nome,
  a: c.dim[0], b: c.dim[1], c: c.dim[2],
  tara: c.tara,
  vao: c.dim[2],
  longa: /^\dM$/.test(c.nome),
}));

const PORID = Object.fromEntries(CAIXAS.map((c) => [c.id, c]));

/** Volume da caixa em m3. */
export const volumeCaixa = (id) => {
  const k = PORID[id];
  return k ? redondo(k.a * k.b * k.c, 5) : 0;
};

/**
 * Metros de perfil que cabem numa caixa, por classe.
 *
 * A classe vem da COR da celula na planilha -- verde, laranja e vermelho -- e
 * nao de uma faixa de peso. Os pesos separam bem hoje (verde ate 0,17; laranja
 * 0,20 a 0,26; vermelho a partir de 0,32), mas isso e coincidencia do catalogo
 * atual: um produto novo pode cair no vao. A cor e a fonte.
 */
export const METROS_POR_CAIXA = { leve: 60, mediano: 45, pesado: 30 };

/** Neon flexivel vai em caixa quadrada, com limite proprio por familia. */
export const METROS_NEON = { 'N 2014': 50, 'N 1525': 50, 'N 1212': 60 };

/** Acima disto, fonte e presilha ganham caixa propria. */
export const KG_AVULSO_JUNTO = 4;

const redondo = (n, casas) => Number(Number(n).toFixed(casas));
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/**
 * Quebra uma quantidade em metros nas barras fisicas que serao embaladas.
 *
 * ISTO NAO E DIVIDIR POR TRES. Kaio, 17/09: "algumas vezes os perfis podem ser
 * descritos como 10m, mas e para indicar uma continuidade conforme o tamanho
 * maximo de corte de 3m, sendo nesse exemplo 3x 3m e 1x 1m". Ou seja, 10 m nao
 * sao dez pecas de 1 m nem uma peca de 10 m: sao tres barras de 3 m e uma de 1 m.
 *
 * Quando o vendedor informa a medida de corte, ela manda -- e o tamanho da peca
 * que o cliente pediu. Ainda assim nenhuma barra passa de MAX_CORTE_M, porque
 * acima disso a fabrica corta de qualquer jeito.
 */
export function barrasDeMetros(metros, medidaCorte = null) {
  const total = num(metros);
  if (total <= 0) return [];
  const corte = Math.min(num(medidaCorte) > 0 ? num(medidaCorte) : MAX_CORTE_M, MAX_CORTE_M);

  const barras = [];
  let resta = total;
  // Tolerancia de 1 mm: 10 / 3 em ponto flutuante deixa um residuo de 1e-15 que
  // viraria uma barra fantasma -- e caixa fantasma vira frete cobrado a mais.
  while (resta > 0.001) {
    const barra = Math.min(corte, resta);
    barras.push(redondo(barra, 3));
    resta = redondo(resta - barra, 3);
  }
  return barras;
}

/** A menor caixa longa em que a barra cabe. */
function caixaParaBarra(comprimento) {
  return CAIXAS.find((k) => k.longa && comprimento <= k.vao + 1e-9) || null;
}

/**
 * Distribui TODAS as barras da proposta em caixas.
 *
 * Recebe as barras de todos os itens juntas, de proposito. A primeira versao
 * empacotava item por item e deu nove caixas para o CP 10439 -- um pedido real
 * de 19,7 m e 12,45 kg que cabe em UMA. A tara saia maior que a mercadoria, o
 * que e o sinal de que a conta esta errada. Vinte e um testes passavam, porque
 * todos usavam um item so.
 *
 * Duas coisas fazem a diferenca:
 *  - barra curta cabe em caixa longa. Um pedaco de 0,2 m nao precisa de caixa
 *    propria se ja existe uma 3M aberta.
 *  - barras de itens diferentes dividem caixa. E o que a expedicao faz.
 *
 * Os DOIS limites valem ao mesmo tempo, e qual deles aperta muda com o produto:
 * um perfil leve estoura os 60 m muito antes dos 17 kg, enquanto o PDL74
 * (1,27 kg/m) bate nos 17 kg com 13 m -- menos da metade dos 30 m da classe.
 * Aplicar so um dos dois subestima o frete em metade do catalogo.
 *
 * Com classes misturadas na mesma caixa vale o limite da MAIS restritiva: a
 * planilha da o limite por classe e nao diz como combinar, e para frete e melhor
 * sobrar caixa do que faltar.
 */
function empacotarBarras(barras) {
  // Maior primeiro: e a barra mais longa que decide o tamanho da caixa, e
  // encaixar as curtas depois e o que evita caixa quase vazia.
  const restantes = [...barras].sort((x, y) => y.comprimento - x.comprimento);
  const caixas = [];

  while (restantes.length > 0) {
    const k = caixaParaBarra(restantes[0].comprimento);
    if (!k) { restantes.shift(); continue; }   // maior que a maior caixa: nao deveria existir

    const caixa = { id: k.id, metros: 0, peso: 0, pecas: 0, classes: new Set() };
    caixas.push(caixa);

    for (let i = 0; i < restantes.length; ) {
      const b = restantes[i];
      const classes = new Set(caixa.classes).add(b.classe);
      const limite = Math.min(...[...classes].map((c) => METROS_POR_CAIXA[c] ?? METROS_POR_CAIXA.pesado));

      // A conferencia do vao e GUARDA, nao regra ativa: como a caixa e aberta
      // pela barra mais longa que restou, nunca aparece depois uma que nao caiba.
      // Quebrar esta linha nao derruba teste nenhum, e isso esta certo -- ela
      // existe para o dia em que alguem mexer na ordenacao.
      const cabe = b.comprimento <= k.vao + 1e-9
        && caixa.metros + b.comprimento <= limite + 1e-9
        && caixa.peso + b.peso <= MAX_KG_POR_CAIXA + 1e-9;

      if (cabe) {
        caixa.metros = redondo(caixa.metros + b.comprimento, 3);
        caixa.peso = redondo(caixa.peso + b.peso, 3);
        caixa.pecas += 1;
        caixa.classes = classes;
        restantes.splice(i, 1);
      } else i++;
    }
  }
  return caixas.map(({ classes, ...c }) => c);
}

/**
 * Monta as caixas de uma lista de itens.
 *
 * Cada item: { nome, quantidade, unidade: 'M' | 'UN', pesoUnitario, classe,
 *              medidaCorte }
 *  - `pesoUnitario` e kg por METRO quando a unidade e 'M', kg por PECA quando e
 *    'UN'. Confundir os dois faz um perfil de 10 m pesar o mesmo que o de 1 m.
 *  - `classe`: 'leve' | 'mediano' | 'pesado' para perfil de aluminio, 'neon'
 *    para flexivel, 'unidade' para fonte, presilha e afins.
 *
 * Itens sem peso NAO entram na conta e voltam em `semPeso`. Chutar um peso faria
 * o frete parecer calculado quando nao esta -- e 20,9% do catalogo nao tem peso.
 */
export function montarCaixas(itens = []) {
  const caixas = [];
  const semPeso = [];
  const barrasDaProposta = [];   // junta os perfis de TODOS os itens: ver empacotarBarras
  let pesoAvulso = 0;
  const avulsos = [];

  for (const it of itens || []) {
    const pesoUnit = num(it?.pesoUnitario);
    const qtd = num(it?.quantidade);
    if (qtd <= 0) continue;
    if (pesoUnit <= 0) { semPeso.push(it?.nome || '(sem nome)'); continue; }

    if (it.unidade === 'UN') {
      // Fonte, presilha, kit: acumula e decide depois, porque a regra dos 4 kg
      // olha o TOTAL do grupo, nao cada item.
      pesoAvulso = redondo(pesoAvulso + qtd * pesoUnit, 3);
      avulsos.push({ nome: it.nome, qtd, peso: redondo(qtd * pesoUnit, 3) });
      continue;
    }

    const limite = it.classe === 'neon'
      ? (METROS_NEON[it.familiaNeon] ?? 50)
      : (METROS_POR_CAIXA[it.classe] ?? METROS_POR_CAIXA.pesado);

    if (it.classe === 'neon') {
      // Neon e flexivel: enrola na caixa quadrada, entao comprimento nao decide
      // caixa -- metros e peso decidem.
      let resta = qtd;
      while (resta > 0.001) {
        const nesta = Math.min(limite, resta, MAX_KG_POR_CAIXA / pesoUnit);
        caixas.push({ id: 'QUADRADA', metros: redondo(nesta, 3), peso: redondo(nesta * pesoUnit, 3), pecas: 1 });
        resta = redondo(resta - nesta, 3);
      }
      continue;
    }

    const classe = METROS_POR_CAIXA[it.classe] ? it.classe : 'pesado';
    for (const comprimento of barrasDeMetros(qtd, it.medidaCorte)) {
      barrasDaProposta.push({ comprimento, peso: redondo(comprimento * pesoUnit, 3), classe });
    }
  }

  caixas.push(...empacotarBarras(barrasDaProposta));

  // Fonte e presilha: ate 4 kg no total vao junto com o resto, e so ganham caixa
  // propria acima disso. Sem nenhuma caixa longa aberta, viram caixa de amostra.
  if (pesoAvulso > 0) {
    if (pesoAvulso <= KG_AVULSO_JUNTO && caixas.length > 0) {
      const folga = caixas.find((c) => c.peso + pesoAvulso <= MAX_KG_POR_CAIXA + 1e-9);
      if (folga) folga.peso = redondo(folga.peso + pesoAvulso, 3);
      else caixas.push({ id: 'AMOSTRA_1', metros: 0, peso: pesoAvulso, pecas: avulsos.length });
    } else {
      let resta = pesoAvulso;
      while (resta > 0.001) {
        const nesta = Math.min(MAX_KG_POR_CAIXA, resta);
        caixas.push({ id: 'QUADRADA', metros: 0, peso: redondo(nesta, 3), pecas: 1 });
        resta = redondo(resta - nesta, 3);
      }
    }
  }

  const metragemCubica = redondo(caixas.reduce((s, c) => s + volumeCaixa(c.id), 0), 4);
  const pesoMercadoria = redondo(caixas.reduce((s, c) => s + c.peso, 0), 3);
  // A tara entra no peso que viaja. Tres caixas 3M sao 6 kg que hoje ninguem soma.
  const tara = redondo(caixas.reduce((s, c) => s + (PORID[c.id]?.tara || 0), 0), 3);

  return {
    caixas,
    quantidadeVolume: caixas.length,
    metragemCubica,
    pesoMercadoria,
    tara,
    pesoTotal: redondo(pesoMercadoria + tara, 3),
    semPeso,
    // Quem desenha a tela precisa saber: com item sem peso, o total esta
    // INCOMPLETO e nao pode ser apresentado como cotacao boa.
    completo: semPeso.length === 0 && caixas.length > 0,
  };
}

// =====================================================================
// Do item da PROPOSTA ao volume (decisoes D1 a D3 do Kaio, 07/10/2026)
// Spec: docs/superpowers/specs/2026-10-07-volumes-e-peso-cubado.md
// =====================================================================

/**
 * Fator de cubagem da Jamef, kg por m3. E o MESMO de `supabase/functions/cotar-frete-jamef/logica.ts`
 * (a Jamef confirmou 300 em 22/09, "pode variar por negociacao"); tests/unit/frete-caixas-da-proposta.test.js
 * compara os dois, porque a funcao roda em Deno e nao pode importar daqui.
 */
export const FATOR_CUBAGEM_KG_M3 = 300;

/**
 * D1 -- a classe do perfil sai do kg/m, nao da cor da celula da planilha (que nao ha como ler daqui).
 * Faixas medidas: leve ate 0,17; mediano 0,20 a 0,26; pesado a partir de 0,32. Os VAOS (0,17-0,20 e 0,26-0,32)
 * caem na classe MAIS PESADA: o limite de metros por caixa fica menor, entao sobra caixa em vez de faltar -- que e o
 * lado seguro para frete. Peso ilegivel nao pode virar "leve" (o limite mais folgado subestimaria o frete).
 */
export const LIMITE_LEVE_KG_M = 0.17;
export const LIMITE_MEDIANO_KG_M = 0.26;
export function classeDoPerfil(kgPorMetro) {
  const kg = Number(kgPorMetro);
  if (!Number.isFinite(kg) || kg <= 0) return 'pesado';
  if (kg <= LIMITE_LEVE_KG_M) return 'leve';
  if (kg <= LIMITE_MEDIANO_KG_M) return 'mediano';
  return 'pesado';
}

/**
 * A medida de corte que o vendedor digita ("2,4m", "2400mm", "150 cm", "1000 x 35 x 35 mm") em metros.
 * A unidade e a ultima que aparece no texto e vale para toda a lista; o comprimento e o PRIMEIRO numero.
 * Sem unidade ou sem numero: nulo, e o empacotador usa o corte maximo de 3 m. Nao adivinho a unidade.
 */
export function medidaDeCorteEmMetros(texto) {
  const s = String(texto ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const numero = /(\d+(?:[.,]\d+)?)/.exec(s);
  const unidades = [...s.matchAll(/\d\s*(mm|cm|m)\b/g)];
  if (!numero || !unidades.length) return null;
  const fator = { mm: 0.001, cm: 0.01, m: 1 }[unidades[unidades.length - 1][1]];
  const metros = Number(numero[1].replace(',', '.')) * fator;
  return metros > 0 && metros <= 50 ? redondo(metros, 3) : null;
}

/** Id da caixa -> nome na tabela de embalagens (`pesos.jsx`), que e o que a tela de volumes guarda em `especie`. */
const nomeDaCaixa = (id) => PORID[id]?.nome || '';

// Perfil SF: peca de N metros escrita no codigo (`PDL SF1210-2M-BR`). Mesma leitura de `pesos.jsx`.
const metrosDoCodigoSF = (codigo) => {
  const cod = String(codigo || '').toUpperCase().replace(/[\s._]/g, '');
  if (!/^PDLSF\d{3,5}/.test(cod)) return 0;
  return num((/-(\d+(?:[.,]\d+)?)M(?:-|$)/.exec(cod) || [])[1]?.replace(',', '.'));
};

// Origens que sao BARRA de perfil. Fita (enrolada), fonte, presilha, bisnaga, Skyline e afins vao como avulsos pelo peso total.
const ORIGENS_BARRA = new Set(['perfil', 'perfil+fita']);

/**
 * Converte os itens da proposta no formato de `montarCaixas`.
 *
 * O peso vem de `pesoUnitarioItem` (a tabela real da PDLED): kg por unidade VENDIDA -- por metro nos perfis em metro,
 * por peca nos demais. Item sem peso sai com `pesoUnitario` 0 e o empacotador o devolve em `semPeso`.
 * A classe e a do PERFIL: em `perfil+fita` o peso que viaja inclui a fita, mas a classe olha so o perfil
 * (PDL12 = 0,16 continua leve com fita de 19,2 W, que dá 0,1792).
 */
export function itensParaCaixas(itens) {
  return (itens || []).map((it) => {
    const nome = it.nome || it.codigo || '(sem nome)';
    const qtd = num(it.quantidade);
    const emMetro = it.idUnidadeMedida === 2 || String(it.unidade || '').toLowerCase() === 'm';
    const p = pesoUnitarioItem(it);
    if (!p) return { nome, quantidade: qtd, unidade: emMetro ? 'M' : 'UN', pesoUnitario: 0 };

    // Peca de N metros vendida por unidade (perfil SF): vira N metros por peca, com corte = a peca.
    if (p.origem === 'perfil SF') {
      const metros = metrosDoCodigoSF(it.codigo);
      if (metros > 0) {
        const pesoUnitario = redondo(p.kg / metros, 4);
        return { nome, quantidade: redondo(qtd * metros, 3), unidade: 'M', pesoUnitario, medidaCorte: metros, classe: classeDoPerfil(pesoUnitario) };
      }
    }

    if (emMetro && ORIGENS_BARRA.has(p.origem)) {
      const neon = /^PDL N (\d{4})/i.exec(String(it.codigo || '').trim());
      const kgBase = p.origem === 'perfil+fita'
        ? (PESOS_PERFIL.find(([k]) => String(p.detalhe || '').startsWith(`${k} +`)) || [])[1] ?? p.kg
        : p.kg;
      return {
        nome, quantidade: qtd, unidade: 'M', pesoUnitario: p.kg,
        medidaCorte: medidaDeCorteEmMetros(it.dimensoes ?? it.medidaCorte),
        classe: neon ? 'neon' : classeDoPerfil(kgBase),
        ...(neon ? { familiaNeon: `N ${neon[1]}` } : {}),
      };
    }

    // Avulso: o que importa e o peso TOTAL (quantidade x kg), seja qual for a unidade de venda.
    return { nome, quantidade: qtd, unidade: 'UN', pesoUnitario: p.kg, classe: 'unidade' };
  });
}

/**
 * D2 -- o que o botao "Sugerir volumes" mostra: UMA linha por caixa, no formato que a tela de volumes ja usa.
 * Devolve tambem o resultado do empacotador (`semPeso`, `completo`, `metragemCubica`...).
 */
export function volumesSugeridos(itens) {
  const r = montarCaixas(itensParaCaixas(itens));
  const volumes = r.caixas.map((c, i) => ({
    id: `vol_s${i}`, qtd: 1, especie: nomeDaCaixa(c.id), marca: 'PDLED', numeracao: String(i + 1).padStart(3, '0'),
    pesoLiquido: c.peso, pesoBruto: 0,
    ref: c.metros > 0 ? `${c.metros} m de perfil` : 'avulsos', itensNoVolume: c.pecas,
  }));
  return { ...r, volumes };
}

/**
 * D3 -- cubagem e peso cubado dos volumes da proposta. Soma qtd x volume de cada caixa conhecida.
 * NULO se nao ha volume ou se algum e de caixa desconhecida ("Outra"): um numero parcial pareceria completo, e o
 * peso cubado e justamente o que o financeiro vai usar para explicar a conta. Arredonda como a funcao da Jamef
 * (peso cubado a 2 casas).
 */
export function cubagemDosVolumes(volumes) {
  const lista = Array.isArray(volumes) ? volumes : [];
  if (!lista.length) return null;
  let m3 = 0;
  for (const v of lista) {
    const caixa = CAIXAS.find((c) => c.nome === v?.especie);
    if (!caixa) return null;
    m3 += num(v.qtd) * volumeCaixa(caixa.id);
  }
  // Metragem: desloca a virgula pelo TEXTO (`e4`), nao `toFixed`: 0,07755 vale 0,0775499... em ponto flutuante e o toFixed
  // devolvia 0,0775 (achado no aceite do staging, 07/10). Peso cubado: a MESMA expressao de `pesoCubado` em
  // cotar-frete-jamef/logica.ts, para o numero gravado ser exatamente o que a cotacao mostraria.
  const metragemCubica = Number(`${Math.round(Number(`${m3}e4`))}e-4`);
  return { metragemCubica, pesoCubado: Math.round(m3 * FATOR_CUBAGEM_KG_M3 * 100) / 100 };
}
